import React, { useState } from 'react';
import { useListContext, useNotify, useRefresh } from 'react-admin';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import IconButton from '@material-ui/core/IconButton';
import BlockIcon from '@material-ui/icons/Block';
import ClearIcon from '@material-ui/icons/Clear';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import DeleteIcon from '@material-ui/icons/Delete';
import callPostRequest from '../api/callPostRequest';

/**
 * Actions for the Free Lookup Administration page, rendered as the `actions` of the react-admin
 * <List> (top-right, next to the page title): "Clear filters", "Block all"/"Unblock all" (every
 * user matching the current filter, not just the checked rows / current page), plus a single
 * "Blocked domains" button opening one combined manage screen - add a new domain (top) and the
 * list of currently blocked domains with a per-row remove button (below), both in the same
 * dialog rather than two separate "Block domain" / "See blocked domains" dialogs.
 * See anna-docs/FREE_LOOKUP_ADMINISTRATION_SPEC.md §5.
 *
 * Blocking a domain does NOT retroactively block existing `anna_isin_user` rows of that domain -
 * this is purely the management UI for `isin.anna_isin_blocked_domain`. It IS meant to be a
 * global block enforced at login/registration time, but that enforcement lives in the separate
 * AnnaIsinLookup app (public self-registration), not here - see
 * `C:\develop\AnnaIsinLookup\TASK_FREE_LOOKUP_ACTIVITY_LOGGING.md`.
 */
interface BlockedDomain {
    domain: string;
    blockedDate?: string;
}

const FreeLookupDomainActions: React.FC = () => {
    const notify = useNotify();
    const refresh = useRefresh();
    const { setFilters, displayedFilters, filterValues, total } = useListContext();

    // Mirrors ../tickets/ClearFilters.tsx: reset every active filter back to an empty value
    // (searchField/dateRange/status then fall back to their defaults server- and UI-side -
    // see FreeLookupAdminService's optString/optBoolean defaults and the "|| 'month'" in
    // DateRangeInput) rather than hiding/removing the filter inputs, since all of ours are
    // alwaysOn.
    const handleClearFilters = () => {
        const clearedFilters = Object.keys(filterValues).reduce((acc, key) => {
            acc[key] = Array.isArray(filterValues[key]) ? [] : '';
            return acc;
        }, {});

        setFilters(clearedFilters, displayedFilters);
    };

    // Which confirmation dialog is open, if any - 'block'/'unblock' both reuse the same dialog and
    // the same endpoint (just a different `blocked` value), see handleSetBlockedAll.
    const [blockAllAction, setBlockAllAction] = useState<'block' | 'unblock' | null>(null);
    const [applyingBlockAllAction, setApplyingBlockAllAction] = useState(false);

    // "Block all"/"Unblock all" act on every user matching the current filter server-side (see
    // FreeLookupAdminService.setBlockedForAllMatchingFilter), not just the rows checked via the
    // grid's row-selection checkboxes (that's FreeLookupBulkActionButtons in FreeLookupAdmin.tsx)
    // and not just the current page - a filtered list can span many pages. `filter` is
    // JSON-encoded the same way the list GET request encodes it (see dataProvider.ts's
    // queryFromParams), so it binds to the same `@RequestParam filter: JSONObject` the list
    // endpoint already uses.
    const handleSetBlockedAll = async () => {
        if (!blockAllAction) return;

        setApplyingBlockAllAction(true);
        try {
            const result = await callPostRequest('/anna/api/v1/freelookupadmin/block-all', {
                filter: JSON.stringify(filterValues),
                blocked: blockAllAction === 'block',
            });
            notify(result.message || 'Users updated', 'info');
            setBlockAllAction(null);
            refresh();
        } catch (error) {
            notify(`Error: ${error.message}`, 'warning');
        } finally {
            setApplyingBlockAllAction(false);
        }
    };

    const [domain, setDomain] = useState('');
    const [blocking, setBlocking] = useState(false);

    const [domainsDialogOpen, setDomainsDialogOpen] = useState(false);
    const [blockedDomains, setBlockedDomains] = useState<BlockedDomain[]>([]);
    const [loadingDomains, setLoadingDomains] = useState(false);

    const handleBlockDomain = async () => {
        setBlocking(true);
        try {
            await callPostRequest('/anna/api/v1/freelookupdomains', { domain });
            notify(`Domain blocked: ${domain}`, 'info');
            setDomain('');
            loadBlockedDomains();
            refresh();
        } catch (error) {
            notify(`Error: ${error.message}`, 'warning');
        } finally {
            setBlocking(false);
        }
    };

    const loadBlockedDomains = () => {
        setLoadingDomains(true);
        fetch('/anna/api/v1/freelookupdomains')
            .then(res => {
                if (!res.ok) throw new Error(`HTTP error ${res.status}`);
                return res.json();
            })
            .then(json => setBlockedDomains(json.data || []))
            .catch(() => notify('Could not load blocked domains', 'warning'))
            .finally(() => setLoadingDomains(false));
    };

    const openDomainsDialog = () => {
        setDomain('');
        setDomainsDialogOpen(true);
        loadBlockedDomains();
    };

    const handleUnblockDomain = async (domainToRemove: string) => {
        try {
            await callPostRequest('/anna/api/v1/freelookupdomains/remove', { domain: domainToRemove });
            notify(`Domain unblocked: ${domainToRemove}`, 'info');
            loadBlockedDomains();
            refresh();
        } catch (error) {
            notify(`Error: ${error.message}`, 'warning');
        }
    };

    return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button startIcon={<ClearIcon />} onClick={handleClearFilters}>
                Clear filters
            </Button>
            <Button
                startIcon={<BlockIcon />}
                disabled={!total}
                onClick={() => setBlockAllAction('block')}
            >
                Block all{total ? ` (${total})` : ''}
            </Button>
            <Button
                startIcon={<LockOpenIcon />}
                disabled={!total}
                onClick={() => setBlockAllAction('unblock')}
            >
                Unblock all{total ? ` (${total})` : ''}
            </Button>
            <Button startIcon={<BlockIcon />} onClick={openDomainsDialog}>
                Blocked domains
            </Button>

            <Dialog open={blockAllAction !== null} onClose={() => setBlockAllAction(null)} fullWidth maxWidth="xs">
                <DialogTitle>
                    {blockAllAction === 'unblock' ? 'Unblock all matching users' : 'Block all matching users'}
                </DialogTitle>
                <DialogContent>
                    {blockAllAction === 'unblock' ? 'Unblock' : 'Block'} all {total} user(s) matching
                    the current filters? This applies across all pages, not just this one.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBlockAllAction(null)}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={applyingBlockAllAction}
                        onClick={handleSetBlockedAll}
                    >
                        {blockAllAction === 'unblock' ? 'Unblock all' : 'Block all'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={domainsDialogOpen} onClose={() => setDomainsDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Blocked domains</DialogTitle>
                <DialogContent>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Domain (e.g. example.com)"
                            fullWidth
                            value={domain}
                            onChange={e => setDomain(e.target.value)}
                        />
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={!domain.trim() || blocking}
                            onClick={handleBlockDomain}
                        >
                            Block
                        </Button>
                    </div>

                    {loadingDomains ? (
                        <CircularProgress />
                    ) : blockedDomains.length === 0 ? (
                        <span>No domains blocked.</span>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Domain</TableCell>
                                    <TableCell>Blocked date</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {blockedDomains.map(d => (
                                    <TableRow key={d.domain}>
                                        <TableCell>{d.domain}</TableCell>
                                        <TableCell>
                                            {d.blockedDate ? new Date(d.blockedDate).toLocaleString() : ''}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                title="Unblock domain"
                                                onClick={() => handleUnblockDomain(d.domain)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDomainsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default FreeLookupDomainActions;
